<template>
  <div class="minimum-value-rule">
    <div class="rule-section">
      <p class="rule-section-title text-white">Value requirements</p>

      <div class="row">
        <div class="col-12">
          <base-input label="Requirement type">
            <segmented-toggle
              variant="pills"
              size="sm"
              :options="modeOptions"
              :value="mode"
              @input="onModeChange"
            ></segmented-toggle>
          </base-input>
        </div>
      </div>

      <div class="row">
        <div class="col-md-4" v-if="mode === 'minimum' || mode === 'range'">
          <base-input label="Minimum value">
            <money-input
              :value="localConfig.min_amount"
              :min="0"
              :precision="2"
              @input="onAmountInput('min_amount', $event)"
            ></money-input>
          </base-input>
        </div>
        <div class="col-md-4" v-if="mode === 'maximum' || mode === 'range'">
          <base-input label="Maximum value">
            <money-input
              :value="localConfig.max_amount"
              :min="0"
              :precision="2"
              @input="onAmountInput('max_amount', $event)"
            ></money-input>
          </base-input>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <base-input label="Applies to">
            <el-select
              v-model="localConfig.applies_to"
              class="select-primary"
              placeholder="Select what to measure"
              @change="emitUpdate"
            >
              <el-option
                class="select-primary"
                value="cart_total"
                label="Whole cart total"
              >
                <div>
                  <strong>Whole cart total</strong>
                  <div class="text-muted small">Sum the value of every line in the cart</div>
                </div>
              </el-option>
              <el-option
                class="select-primary"
                value="gift_cards"
                label="Gift cards only"
              >
                <div>
                  <strong>Gift cards only</strong>
                  <div class="text-muted small">Sum only the gift card value in the cart</div>
                </div>
              </el-option>
            </el-select>
          </base-input>
        </div>
      </div>

      <div class="row mt-2">
        <div class="col">
          <p class="small mb-0 helper-text text-white">
            <i class="tim-icons el-icon-info mr-1"></i>
            <span v-if="mode === 'range'">
              The promo only applies when the {{ scopeLabel }} falls within these limits.
            </span>
            <span v-else-if="mode === 'maximum'">
              The promo only applies when the {{ scopeLabel }} is at or below this amount.
            </span>
            <span v-else>
              The promo only applies when the {{ scopeLabel }} reaches this amount.
            </span>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import MoneyInput from '@/components/Inputs/MoneyInput.vue'

interface MinValueConfig {
  min_amount: number | null
  max_amount: number | null
  applies_to: 'cart_total' | 'gift_cards'
  error_message: string
}

type Mode = 'minimum' | 'maximum' | 'range'

const props = defineProps({
  ruleConfig: { type: Object, required: true }
})

const emit = defineEmits<{
  (e: 'update', config: MinValueConfig, mode: Mode): void
}>()

const modeOptions = [
  { value: 'minimum', label: 'Minimum' },
  { value: 'maximum', label: 'Maximum' },
  { value: 'range', label: 'Range' }
]

const mode = ref<Mode>('minimum')
const localConfig = ref<MinValueConfig>({
  min_amount: null,
  max_amount: null,
  applies_to: 'cart_total',
  error_message: ''
})

const scopeLabel = computed(() =>
  localConfig.value.applies_to === 'gift_cards' ? 'gift card value in the cart' : 'cart total'
)

function deriveMode(config: MinValueConfig): Mode {
  const hasMin = config.min_amount != null
  const hasMax = config.max_amount != null
  if (hasMin && hasMax) return 'range'
  if (hasMax) return 'maximum'
  return 'minimum'
}

// Derive the mode once per mount (the modal unmounts this component on close,
// so every open re-derives from the saved config). Re-deriving on every
// round-trip would collapse an explicitly chosen Range back to a single-bound
// mode the moment the operator clears one field to re-type it.
const modeInitialized = ref(false)
watch(
  () => props.ruleConfig,
  (newVal) => {
    if (newVal) {
      localConfig.value = { ...localConfig.value, ...(newVal as Partial<MinValueConfig>) }
      if (!modeInitialized.value) {
        mode.value = deriveMode(localConfig.value)
        modeInitialized.value = true
      }
    }
  },
  { immediate: true, deep: true }
)

function onModeChange(next: Mode) {
  mode.value = next
  if (next === 'minimum') {
    if (localConfig.value.min_amount == null) localConfig.value.min_amount = 0
    localConfig.value.max_amount = null
  } else if (next === 'maximum') {
    if (localConfig.value.max_amount == null) localConfig.value.max_amount = 0
    localConfig.value.min_amount = null
  } else {
    if (localConfig.value.min_amount == null) localConfig.value.min_amount = 0
    if (localConfig.value.max_amount == null) localConfig.value.max_amount = localConfig.value.min_amount || 0
  }
  emitUpdate()
}

function onAmountInput(field: 'min_amount' | 'max_amount', value: number | null) {
  localConfig.value[field] = value
  emitUpdate()
}

function emitUpdate() {
  emit('update', { ...localConfig.value }, mode.value)
}
</script>

<style scoped>
.rule-section {
  padding: 1rem 1.25rem;
  background: rgba(128, 128, 128, 0.08);
  border-radius: 0.375rem;
  border-left: 3px solid #e14eca;
  margin-bottom: 1rem;
}

.rule-section-title {
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 1rem 0;
  color: #fff;
}

.minimum-value-rule >>> label {
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
}

.minimum-value-rule >>> .form-group label {
  color: rgba(255, 255, 255, 0.8);
}

.minimum-value-rule >>> .el-input__inner {
  color: #fff;
}

.minimum-value-rule .text-muted {
  color: rgba(255, 255, 255, 0.5) !important;
}

.helper-text {
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.4;
}
</style>
